import React, { FunctionComponent, ReactNode, useState } from 'react';
import { Collapse, List, ListItemIcon, ListItemText } from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { useNavigate } from 'react-router';
import sideBarListItemStyles from 'styles/sideBarListItem';
import shortid from 'shortid';
import { ProtectedListItem } from 'components/Authorization/components/ProtectedListItem';
import clsx from 'clsx';

interface ProtectedSidebarListItemI {
  title: string;
  icon: ReactNode;
  nestedItems?: {
    name: string;
    guid: string;
    icon: ReactNode;
    lnsResource?: string;
    lnsAction?: string;
  }[];
  sideBarOpen: boolean;
  handleDrawerOpen?: () => void;
  onClickMain?: () => void;
  onClickNestedItem?: (item: $Lns.StoryPool | $Lns.Rundown) => void;
  linkPath?: string;
  nestedItemsPath?: string;
  lnsResource?: string;
  lnsAction?: string;
  selected?: string;
}

export const ProtectedSidebarListItem: FunctionComponent<ProtectedSidebarListItemI> =
  ({
    title,
    icon,
    nestedItems,
    sideBarOpen,
    handleDrawerOpen,
    onClickMain,
    onClickNestedItem,
    linkPath,
    nestedItemsPath,
    lnsResource,
    lnsAction,
    selected
  }: ProtectedSidebarListItemI) => {
    const classes = sideBarListItemStyles();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const handleClick = () => {
      setOpen(!open);
      if (onClickMain) onClickMain();
      if (linkPath) {
        navigate(linkPath);
      }
      if (!sideBarOpen && handleDrawerOpen) {
        handleDrawerOpen();
      }
    };

    const nestedLinkSelected = (guid: string) => {
      if (!guid || !nestedItemsPath) return false;
      if (guid === selected) return true;
      return false;
    };

    const linkSelected = () => {
      if (
        linkPath &&
        selected === undefined &&
        window.location.pathname.includes(linkPath)
      ) {
        return true;
      }

      return false;
    };

    return (
      <>
        <ProtectedListItem
          className={clsx(classes.item, { [classes.open]: sideBarOpen })}
          selected={linkSelected()}
          button
          key={title}
          disableGutters
          onClick={handleClick}
          lnsResource={lnsResource}
          lnsAction={lnsAction}
        >
          <ListItemIcon
            className={clsx(classes.listIcon, {
              [classes.openIcon]: sideBarOpen
            })}
          >
            {icon}
          </ListItemIcon>
          <ListItemText
            primary={title}
            primaryTypographyProps={{ color: 'secondary' }}
          />
          {nestedItems &&
            (open ? (
              <ExpandLess color="secondary" />
            ) : (
              <ExpandMore color="secondary" />
            ))}
        </ProtectedListItem>
        {nestedItems && (
          <Collapse
            className={classes.collapse}
            in={open && sideBarOpen}
            timeout="auto"
            unmountOnExit
          >
            <List component="div" disablePadding>
              {nestedItems.map(listItem => {
                return (
                  <ProtectedListItem
                    className={classes.nested}
                    selected={nestedLinkSelected(listItem.guid)}
                    key={shortid.generate()}
                    button
                    lnsResource={listItem.lnsResource}
                    lnsAction={listItem.lnsAction}
                    onClick={() =>
                      onClickNestedItem && onClickNestedItem(listItem)
                    }
                  >
                    <ListItemIcon className={classes.openIcon}>
                      {listItem.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={listItem.name}
                      primaryTypographyProps={{ color: 'secondary' }}
                    />
                  </ProtectedListItem>
                );
              })}
            </List>
          </Collapse>
        )}
      </>
    );
  };

ProtectedSidebarListItem.defaultProps = {
  nestedItems: undefined,
  onClickNestedItem: undefined,
  linkPath: undefined,
  nestedItemsPath: undefined,
  lnsResource: undefined,
  lnsAction: undefined,
  selected: undefined,
  onClickMain: undefined,
  handleDrawerOpen: undefined
};
